import React from 'react';
import { Link } from 'react-router-dom';

export default function About() {
  const stats = [
    { number: "8+", label: "Years Experience", icon: "💼" },
    { number: "10+", label: "Projects Completed", icon: "🚀" },
    { number: "5G", label: "Telecom Systems", icon: "📡" },
    { number: "Full", label: "Stack Developer", icon: "🛠️" }
  ];

  const education = [
    {
      degree: "M.Tech. – Solid State Electronics Material",
      institution: "Indian Institute of Technology (IIT) Roorkee",
      year: "2016",
      details: "Specialized in advanced materials science with research in electronic properties of semiconductors",
      icon: "🎓"
    },
    {
      degree: "B.Tech. – Electronics & Telecommunication",
      institution: "College of Engineering Roorkee",
      year: "2013",
      details: "Focused on communication systems, digital signal processing, and embedded systems",
      icon: "📚"
    }
  ];

  const passions = [
    {
      title: "System Architecture",
      description: "Designing scalable, maintainable systems that solve complex business problems",
      icon: "🏗️"
    },
    {
      title: "Mentorship",
      description: "Guiding junior developers and sharing knowledge to build stronger engineering teams",
      icon: "👨‍🏫"
    },
    {
      title: "Open Source",
      description: "Contributing to community projects and building tools that help other developers",
      icon: "🔓"
    },
    {
      title: "Continuous Learning",
      description: "Staying updated with emerging technologies and industry best practices",
      icon: "📖"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-10 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                  About Me
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Senior Software Engineer with a passion for building scalable, high-performance applications 
                  that solve real-world problems across diverse domains.
                </p>
              </div>

              {/* Bio */}
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  With over <strong>8 years of experience</strong> in software development, I specialize in 
                  <strong> Java, Spring Boot, and microservices architecture</strong>. My journey has taken me 
                  from building enterprise logistics systems to developing core 5G network infrastructure at 
                  Reliance Jio.
                </p>
                <p>
                  I thrive on <strong>complex technical challenges</strong> and have extensive experience in 
                  <strong> system design, distributed systems, and cloud-native development</strong>. My 
                  background in electronics from IIT Roorkee gives me a unique perspective on software-hardware 
                  integration and telecommunications systems.
                </p>
                <p>
                  When I'm not coding, I enjoy <strong>mentoring junior developers</strong>, contributing to 
                  <strong> open-source projects</strong>, and staying at the forefront of emerging technologies 
                  in the Java ecosystem and beyond.
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/experience"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
                >
                  <span>View Experience</span>
                  <span>💼</span>
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold hover:border-blue-500 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Get In Touch</span>
                  <span>💬</span>
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-md rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Passions Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            What Drives Me
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {passions.map((passion, index) => (
              <div
                key={index}
                className="text-center group hover:transform hover:scale-105 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                  <span className="text-2xl text-white">{passion.icon}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {passion.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {passion.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Education & Background
          </h2>
          <div className="space-y-8">
            {education.map((edu, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-xl text-white">{edu.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {edu.degree}
                        </h3>
                        <p className="text-lg text-blue-600 font-semibold">
                          {edu.institution}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {edu.details}
                    </p>
                  </div>
                  <div className="lg:text-right">
                    <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold text-sm">
                      {edu.year}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl p-12 text-white shadow-2xl">
            <span className="text-6xl mb-6 block">🎯</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              My Engineering Philosophy
            </h2>
            <p className="text-xl leading-relaxed opacity-90 max-w-3xl mx-auto">
              "I believe in writing clean, maintainable code that not only solves today's problems 
              but also anticipates tomorrow's challenges. Great software is built through collaboration, 
              continuous learning, and a deep understanding of both technical requirements and business goals."
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Clean Code</span>
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Scalable Architecture</span>
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">Continuous Improvement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Let's Build Something Amazing Together
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether you need a complex backend system, a full-stack application, or technical guidance, 
            I'm here to help bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/projects"
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Explore My Projects
            </Link>
            <Link
              to="/contact"
              className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold hover:border-blue-500 hover:text-blue-600 transition-all duration-300"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}